import { useState } from "react";
import { formatSEK0 } from "../lib/format";

interface PieSlice {
  label: string;
  value: number;
  color: string;
  key?: string;
}

interface PieChartProps {
  data: PieSlice[];
  title?: string;
  onSliceClick?: (key: string) => void;
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

function describeArc(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  startAngle: number,
  endAngle: number
): string {
  if (endAngle - startAngle >= 359.99) {
    endAngle = startAngle + 359.99;
  }

  const outerStart = polarToCartesian(cx, cy, outerR, endAngle);
  const outerEnd = polarToCartesian(cx, cy, outerR, startAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerR, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 0 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 1 ${innerStart.x} ${innerStart.y}`,
    "Z",
  ].join(" ");
}

export function PieChart({ data, title, onSliceClick }: PieChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Ingen data
      </div>
    );
  }

  const cx = 100;
  const cy = 100;
  const outerR = 90;
  const innerR = 55;

  let currentAngle = 0;
  const slices = data
    .filter((d) => d.value > 0)
    .map((d, i) => {
      const angle = (d.value / total) * 360;
      const startAngle = currentAngle;
      currentAngle += angle;
      return {
        ...d,
        startAngle,
        endAngle: currentAngle,
        index: i,
      };
    });

  return (
    <div className="space-y-4">
      {title && <h3 className="font-semibold text-lg">{title}</h3>}
      <div className="flex flex-col xl:flex-row items-center gap-6">
        <svg viewBox="0 0 200 200" className="w-56 h-56 shrink-0">
          {slices.map((slice) => (
            <path
              key={slice.label}
              d={describeArc(
                cx,
                cy,
                activeIndex === slice.index ? outerR + 4 : outerR,
                innerR,
                slice.startAngle,
                slice.endAngle
              )}
              fill={slice.color}
              opacity={
                activeIndex === null || activeIndex === slice.index ? 1 : 0.4
              }
              className={`transition-all duration-150 ${onSliceClick ? "cursor-pointer" : "cursor-default"}`}
              onMouseEnter={() => setActiveIndex(slice.index)}
              onMouseLeave={() => setActiveIndex(null)}
              onClick={() =>
                onSliceClick && slice.key && onSliceClick(slice.key)
              }
            />
          ))}
          <text
            x={cx}
            y={cy - 6}
            textAnchor="middle"
            className="fill-foreground text-[10px] font-semibold"
          >
            {formatSEK0(total)}
          </text>
          <text
            x={cx}
            y={cy + 8}
            textAnchor="middle"
            className="fill-muted-foreground text-[6px]"
          >
            totalt
          </text>
        </svg>

        <div className="grid grid-cols-1 gap-y-1.5 text-sm w-full">
          {slices.map((slice) => (
            <div
              key={slice.label}
              className={`flex items-center gap-2 transition-opacity ${
                onSliceClick ? "cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1" : ""
              } ${
                activeIndex !== null && activeIndex !== slice.index
                  ? "opacity-40"
                  : ""
              }`}
              onMouseEnter={() => setActiveIndex(slice.index)}
              onMouseLeave={() => setActiveIndex(null)}
              onClick={() =>
                onSliceClick && slice.key && onSliceClick(slice.key)
              }
            >
              <div
                className="w-3 h-3 rounded-sm shrink-0"
                style={{ backgroundColor: slice.color }}
              />
              <span className="truncate" title={slice.label}>{slice.label}</span>
              <span className="text-muted-foreground ml-auto tabular-nums whitespace-nowrap">
                {formatSEK0(slice.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
